var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlert } from 'components/alert/AlertStack';
import { Content } from 'components/Content';
import { EmptyData } from 'components/EmptyData';
import { Header } from 'components/Header';
import { Screen } from 'components/Screen';
import { Spinner } from 'components/Spinner';
import { useDomain } from 'components/domain/DomainProvider';
import { DealPublicQuery } from 'graphql/Deal/queries/DealPublicQuery';
import { CreateDealUpsellPaymentMutation } from 'graphql/Deal/mutations/CreateDealUpsellPaymentMutation';
import { classNames, useEffect, useMutation, useQuery, useRef, useState } from 'libs/react';
import { React } from 'libs/react';
import { useLocation, useParams } from 'react-router-dom';
import i18n from 'components/translation/i18n';
import { StoreConfirmation } from './StoreConfirmation';
import { StoreHeader } from './StoreHeader';
import { StorePaymentPanel } from './StorePaymentPanel';
import { StoreProposedOptionsSection } from './StoreProposedOptionsSection';
import { buildStoreSteps } from './storeSteps';
import { buildUpsellMutationOptions, dealForUpsellPaymentPreview, upsellSelectionFromDeal } from './storeUpsell';
import './StoreScreen.scss';
import { applyBrandColors, resolvePrimaryColor } from 'helpers/primaryColor';
/**
 * @component StoreUpsellScreen
 * @since 1.0.0
 */
export function StoreUpsellScreen() {
    var _a, _b, _c;
    const alert = useAlert();
    const { lang, id } = useParams();
    const location = useLocation();
    const language = lang === 'fr' ? 'fr' : 'en';
    i18n.changeLanguage(language);
    const { logo, options, configurationFields } = useDomain();
    const sessionId = new URLSearchParams(location.search).get('session_id');
    const paymentId = new URLSearchParams(location.search).get('payment_id');
    const [currentStepId, setCurrentStepId] = useState('options');
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectionReady, setSelectionReady] = useState(false);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [checkoutSessionKey, setCheckoutSessionKey] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const stripeReturnHandledRef = useRef(false);
    applyBrandColors({
        primary: resolvePrimaryColor(options.color, '#F05B22'),
        accent: options.accentColor,
        surface: options.surfaceColor
    });
    useEffect(() => {
        document.body.classList.toggle('loaded', true);
    }, []);
    const [dealData, dealReady, dealError, , refetchDeal] = useQuery(DealPublicQuery, {
        variables: {
            input: {
                id,
                skipNotification: true
            }
        },
        fetchPolicy: 'network-only'
    });
    const [createUpsellPayment] = useMutation(CreateDealUpsellPaymentMutation);
    const deal = dealData === null || dealData === void 0 ? void 0 : dealData.deal;
    const hasActiveUpsellPayment = (_b = (_a = deal === null || deal === void 0 ? void 0 : deal.payments) === null || _a === void 0 ? void 0 : _a.some((payment) => payment.status === 'ACTIVE' && payment.description === 'Upsell')) !== null && _b !== void 0 ? _b : false;
    useEffect(() => {
        if (!deal || selectionReady) {
            return;
        }
        const selection = upsellSelectionFromDeal(deal);
        setSelectedItems(selection.selectedItems);
        setSelectionReady(true);
    }, [deal, selectionReady]);
    useEffect(() => {
        if (!sessionId || !id || stripeReturnHandledRef.current) {
            return;
        }
        stripeReturnHandledRef.current = true;
        fetch(process.env.API_URL_ROOT +
            `/stripe-session-status?id=${id}&session_id=${sessionId}&payment_id=${paymentId !== null && paymentId !== void 0 ? paymentId : ''}`, { method: 'POST' })
            .then((res) => res.json())
            .then(() => {
            setPaymentComplete(true);
            setCurrentStepId('payment');
            refetchDeal();
            const params = new URLSearchParams(location.search);
            params.delete('session_id');
            params.delete('payment_id');
            window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
        })
            .catch((error) => {
            alert.present({ error });
        });
    }, [sessionId, id, paymentId]);
    useEffect(() => {
        if (deal &&
            (deal.status === 'WON' || deal.stage === 'COMPLETED') &&
            hasActiveUpsellPayment &&
            !paymentComplete) {
            setCurrentStepId('payment');
        }
    }, [deal, hasActiveUpsellPayment, paymentComplete]);
    useEffect(() => {
        if (currentStepId !== 'payment' || paymentComplete || !deal) {
            setCheckoutSessionKey(null);
            return;
        }
        if (!hasActiveUpsellPayment) {
            return;
        }
        setCheckoutSessionKey((current) => current !== null && current !== void 0 ? current : `${deal.id}-upsell-${Date.now()}`);
    }, [currentStepId, paymentComplete, deal, hasActiveUpsellPayment]);
    const storeSteps = buildStoreSteps(true, t('Payment')).filter((step) => step.id === 'options' || step.id === 'payment');
    const saleItems = deal ? upsellSelectionFromDeal(deal).saleItems : [];
    const eligible = deal &&
        (deal.status === 'WON' || deal.stage === 'COMPLETED') &&
        saleItems.length > 0;
    const activeUpsellPayment = (_c = deal === null || deal === void 0 ? void 0 : deal.payments) === null || _c === void 0 ? void 0 : _c.find((payment) => payment.status === 'ACTIVE' && payment.description === 'Upsell');
    const paymentPreviewDeal = deal && activeUpsellPayment
        ? dealForUpsellPaymentPreview(deal, selectedItems, activeUpsellPayment.amount)
        : deal;
    function handleContinueToPayment() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!deal || submitting) {
                return;
            }
            const optionsPayload = buildUpsellMutationOptions(deal, selectedItems);
            if (optionsPayload.length === 0) {
                alert.present({
                    title: React.createElement("p", null, t('Please select at least one option.'))
                });
                return;
            }
            setSubmitting(true);
            try {
                yield createUpsellPayment({
                    variables: {
                        input: {
                            id: deal.id,
                            options: optionsPayload
                        }
                    }
                });
                yield refetchDeal();
                setCurrentStepId('payment');
            }
            catch (error) {
                alert.present({ error: error });
            }
            finally {
                setSubmitting(false);
            }
        });
    }
    if (!dealReady) {
        return (React.createElement(Screen, { className: "store-screen", ready: true },
            React.createElement(Content, null,
                React.createElement("div", { className: "store-proposed-options-section" },
                    React.createElement(Spinner, null)))));
    }
    if (dealError || !deal) {
        return (React.createElement(EmptyData, { title: t('This order was not found.'), description: t('Please contact us for more information.') }));
    }
    if (!eligible && !hasActiveUpsellPayment && !paymentComplete) {
        return (React.createElement(Screen, { className: "store-screen", ready: true },
            React.createElement(Content, null,
                React.createElement(Header, null,
                    React.createElement(StoreHeader, { configurationFields: configurationFields, logo: logo, currentStepId: "options", steps: storeSteps, isSuccessScreen: true })),
                React.createElement(EmptyData, { title: t('No extras are available for this order.'), description: t('All suggested options have already been added, or this purchase cannot be enhanced.') }))));
    }
    return (React.createElement(Screen, { className: classNames('store-screen', {
            'store-screen--checkout-step': currentStepId === 'payment'
        }), ready: true },
        React.createElement(Content, null,
            React.createElement(Header, null,
                React.createElement(StoreHeader, { configurationFields: configurationFields, logo: logo, currentStepId: currentStepId, steps: storeSteps, isSuccessScreen: paymentComplete })),
            paymentComplete && (React.createElement(StoreConfirmation, { deal: deal, hasPayment: true, hideUpsell: true })),
            !paymentComplete && currentStepId === 'options' && (React.createElement(StoreProposedOptionsSection, { lang: language, hasDirectSales: true, saleItems: saleItems, selectedItems: selectedItems, extrasOnly: true, nextLabel: t('Continue to payment'), processing: submitting, onSelectionChange: setSelectedItems, onNext: handleContinueToPayment })),
            !paymentComplete &&
                currentStepId === 'payment' &&
                paymentPreviewDeal && (React.createElement(StorePaymentPanel, { deal: paymentPreviewDeal, returnPath: `store/${language}/enhance/${deal.id}`, checkoutSessionKey: checkoutSessionKey !== null && checkoutSessionKey !== void 0 ? checkoutSessionKey : `${deal.id}-upsell`, configurationFields: configurationFields, saleItems: saleItems, onBack: () => setCurrentStepId('options'), onPaymentCompleted: () => {
                    setPaymentComplete(true);
                    setCurrentStepId('payment');
                    refetchDeal();
                }, onPaymentSimulated: () => {
                    setPaymentComplete(true);
                    setCurrentStepId('payment');
                    refetchDeal();
                } })))));
}
